import { UcoastEl } from '@/scripts/core/UcoastEl'
import { readQuizResults, matchScoreRange } from './longevity-score-model'

export class QuizBundleRecommendation extends UcoastEl {
	static htmlSelector = 'quiz-bundle-recommendation'
	private observer?: MutationObserver
	private refresh = () => this.renderRecommendation()

	override connectedCallback() {
		super.connectedCallback()
		this.renderRecommendation()
		window.addEventListener('popstate', this.refresh)
		window.addEventListener('pageshow', this.refresh)
		// A hero can be reconfigured, removed, or replaced independently in the theme editor.
		this.observer = new MutationObserver((mutations) => {
			if (mutations.some((mutation) => {
				const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement
				return target?.closest('longevity-score-hero') || [...mutation.addedNodes, ...mutation.removedNodes].some((node) =>
					node instanceof Element && (node.matches('longevity-score-hero') || node.querySelector('longevity-score-hero')),
				)
			})) this.renderRecommendation()
		})
		this.observer.observe(document.body, {
			childList: true, subtree: true, characterData: true, attributes: true,
			attributeFilter: ['data-uc-parameter-total', 'data-uc-parameter-product', 'data-uc-preview-score', 'data-uc-range-min', 'data-uc-range-max'],
		})
	}

	override disconnectedCallback() {
		this.observer?.disconnect()
		window.removeEventListener('popstate', this.refresh)
		window.removeEventListener('pageshow', this.refresh)
		super.disconnectedCallback()
	}

	private renderRecommendation() {
		const editor = this.dataset.ucDesignMode === 'true'
		const heroes = Array.from(document.querySelectorAll<HTMLElement>('longevity-score-hero'))
		const heroId = this.dataset.ucHeroId?.trim()
		const hero = heroId ? heroes.find((element) => element.id === heroId) : heroes.length === 1 ? heroes[0] : null
		const label = this.dataset.ucRecommendationLabel?.trim() || ''
		let selected: { copy: Element | null } | null = null
		const buyBox = this.closest<HTMLElement>('longevity-results-buy-box')
		// A second approved label must never attach this body's copy to a different page product.
		const productMatches = !buyBox || !!buyBox.dataset.ucProductHandle?.trim()
			&& buyBox.dataset.ucProductLabel?.trim().toLocaleLowerCase() === label.toLocaleLowerCase()
			&& buyBox.dataset.ucProductParameter?.trim() === hero?.dataset.ucParameterProduct?.trim()
		if (hero && label && productMatches) {
			const mapping = { total: hero.dataset.ucParameterTotal || '', product: hero.dataset.ucParameterProduct || '' }
			const params = new URLSearchParams(window.location.search)
			const result = readQuizResults(params, mapping)
			if (editor && !params.has(mapping.total)) result.total = Number(hero.dataset.ucPreviewScore)
			if (editor && !params.has(mapping.product)) result.product = label
			if (mapping.total.trim() && mapping.product.trim() && result.product?.toLocaleLowerCase() === label.toLocaleLowerCase()) {
				// Read ALL merchant-configured ranges, not the hero's currently displayed selection.
				const ranges = Array.from(hero.querySelectorAll<HTMLElement>('[data-uc-score-description]')).map((element) => ({
					min: Number(element.dataset.ucRangeMin), max: Number(element.dataset.ucRangeMax),
					copy: element.querySelector('[data-uc-description-copy]'),
				})).filter((range) => range.copy?.textContent?.trim())
				selected = matchScoreRange(result.total, ranges)
			}
		}
		const description = this.querySelector<HTMLElement>('[data-uc-recommendation-description]')!
		const body = this.querySelector<HTMLElement>('[data-uc-recommendation-body]')!
		const eyebrow = this.querySelector<HTMLElement>('.bundle-buy-box__recommendation-eyebrow')!
		const defaultBody = this.querySelector<HTMLTemplateElement>('[data-uc-recommendation-body-copy="default"]')!
		// One configurable body belongs to the approved bundle, never a score slot.
		const bodyCopy = defaultBody.content.textContent?.trim() ? defaultBody : null
		const matched = !!selected?.copy && !!bodyCopy
		// Only clone merchant-authored Liquid rich text. URL values are never inserted as HTML.
		description.replaceChildren(...(matched ? Array.from(selected!.copy!.childNodes).map((node) => node.cloneNode(true)) : []))
		body.replaceChildren(...(matched ? [bodyCopy!.content.cloneNode(true)] : []))
		eyebrow.textContent = matched ? defaultBody.dataset.ucEyebrow || '' : ''
		description.hidden = body.hidden = !matched
		eyebrow.hidden = !matched || !eyebrow.textContent.trim()
		const warning = this.querySelector<HTMLElement>('[data-uc-quiz-editor-warning]')!
		warning.textContent = !hero
			? 'Add one Longevity Score Hero or set its exact element ID. No recommendation is shown without a unique source.'
			: !productMatches ? 'Match this block’s approved recommendation label and the hero product parameter to the configured page product and section recommendation settings. This recommendation is hidden on the storefront.'
			: selected && !bodyCopy ? 'Add a bundle body for the configured page product. Archived score-range copy is not used. This recommendation is hidden on the storefront.'
			: 'Configure the hero score ranges and Digioh fields, and match this block’s approved recommendation label. Missing or unsupported results hide this block on the storefront.'
		warning.hidden = !editor || matched
		this.dataset.ucRecommendationState = matched ? 'matched' : 'unavailable'
		this.closest<HTMLElement>('[data-uc-results-block]')!.hidden = !matched && !editor
	}
}
